import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { recruitment } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import { useDepartments } from '@/hooks/useOrg'
import type {
  CreateApplicantPayload,
  CreateRequisitionPayload,
  RequisitionStatus,
  StageChangePayload,
  UpdateApplicantPayload,
  UpdateRequisitionPayload,
} from '@/lib/types'

export const recruitmentKeys = {
  all: ['recruitment'] as const,
  requisitions: (params: object | undefined) =>
    ['recruitment', 'requisitions', params ?? null] as const,
  requisition: (id: number) => ['recruitment', 'requisition', id] as const,
  board: (id: number) => ['recruitment', 'board', id] as const,
  applicant: (id: number) => ['recruitment', 'applicant', id] as const,
  history: (id: number) => ['recruitment', 'history', id] as const,
  attachments: (id: number) => ['recruitment', 'attachments', id] as const,
}

/** True when the current user is Department.head_user_id of at least one department. */
export function useIsDeptHead(): { isDeptHead: boolean; headedDepartmentIds: number[] } {
  const { user } = useAuth()
  const { data } = useDepartments()
  const ids = user ? (data ?? []).filter((d) => d.head_user_id === user.id).map((d) => d.id) : []
  return { isDeptHead: ids.length > 0, headedDepartmentIds: ids }
}

export function useRequisitions(params?: {
  status?: RequisitionStatus
  department_id?: number
  page?: number
  page_size?: number
}) {
  return useQuery({
    queryKey: recruitmentKeys.requisitions(params),
    queryFn: async () => (await recruitment.listRequisitions(params)).data,
  })
}

export function useRequisition(id: number | null) {
  return useQuery({
    queryKey: id ? recruitmentKeys.requisition(id) : ['recruitment', 'requisition', 'noop'],
    queryFn: async () => (await recruitment.getRequisition(id as number)).data,
    enabled: id != null,
  })
}

export function useRecruitmentBoard(id: number | null) {
  return useQuery({
    queryKey: id ? recruitmentKeys.board(id) : ['recruitment', 'board', 'noop'],
    queryFn: async () => (await recruitment.board(id as number)).data,
    enabled: id != null,
  })
}

export function useApplicant(id: number | null) {
  return useQuery({
    queryKey: id ? recruitmentKeys.applicant(id) : ['recruitment', 'applicant', 'noop'],
    queryFn: async () => (await recruitment.getApplicant(id as number)).data,
    enabled: id != null,
  })
}

export function useApplicantHistory(id: number | null) {
  return useQuery({
    queryKey: id ? recruitmentKeys.history(id) : ['recruitment', 'history', 'noop'],
    queryFn: async () => (await recruitment.history(id as number)).data,
    enabled: id != null,
  })
}

export function useApplicantAttachments(id: number | null) {
  return useQuery({
    queryKey: id ? recruitmentKeys.attachments(id) : ['recruitment', 'attachments', 'noop'],
    queryFn: async () => (await recruitment.listAttachments(id as number)).data,
    enabled: id != null,
  })
}

export function useCreateRequisition() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreateRequisitionPayload) =>
      (await recruitment.createRequisition(payload)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: recruitmentKeys.all }),
  })
}

export function useUpdateRequisition(id: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: UpdateRequisitionPayload) =>
      (await recruitment.updateRequisition(id, payload)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: recruitmentKeys.all }),
  })
}

export function useCreateApplicant() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreateApplicantPayload) =>
      (await recruitment.createApplicant(payload)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: recruitmentKeys.all }),
  })
}

export function useUpdateApplicant(id: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: UpdateApplicantPayload) =>
      (await recruitment.updateApplicant(id, payload)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: recruitmentKeys.all }),
  })
}

/** Stage moves can create employees, trackers, notifications and calendar events. */
export function useChangeStage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { id: number; payload: StageChangePayload }) =>
      (await recruitment.changeStage(vars.id, vars.payload)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recruitmentKeys.all })
      queryClient.invalidateQueries({ queryKey: ['onboarding'] })
      queryClient.invalidateQueries({ queryKey: ['employees'] })
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

export function useUploadApplicantAttachment(id: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => (await recruitment.uploadAttachment(id, file)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: recruitmentKeys.all }),
  })
}

export function isStaffRole(role: string | undefined | null): boolean {
  return role === 'admin' || role === 'manager' || role === 'handler'
}
